import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, X, Phone } from "lucide-react";
import api from "../lib/api";
import { getUserId } from "../lib/user";
import { createPeer, openSignal } from "../lib/webrtc";
import { Button } from "../components/ui/button";

export default function LiveJoin() {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [hostOnline, setHostOnline] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const streamRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const hostIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/meetings/${id}`).then((r) => { if (!cancelled) setMeeting(r.data); }).catch(() => toast.error("Cannot access this meeting"));
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let alive = true;
    let iceQueue = [];

    async function setup() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      } catch {
        toast.error("Camera/mic permission required to join");
        setStatus("denied");
        return;
      }

      const pc = createPeer({
        stream: streamRef.current,
        onTrack: (s) => { if (remoteVideoRef.current) remoteVideoRef.current.srcObject = s; setStatus("connected"); },
        onIce: (cand) => {
          if (wsRef.current?.readyState === 1 && hostIdRef.current) {
            wsRef.current.send(JSON.stringify({ type: "ice", to: hostIdRef.current, candidate: cand }));
          }
        },
      });
      pcRef.current = pc;

      const ws = openSignal({
        meetingId: id,
        userId: getUserId(),
        role: "guest",
        onMessage: async (msg) => {
          if (msg.type === "room-state") {
            const host = (msg.peers || []).find((p) => p.role === "host");
            if (host) {
              hostIdRef.current = host.user_id;
              setHostOnline(true);
              ws.send(JSON.stringify({ type: "ready", to: host.user_id }));
            } else {
              setStatus("waiting-for-host");
            }
          } else if (msg.type === "peer-joined" && msg.role === "host") {
            hostIdRef.current = msg.from;
            setHostOnline(true);
            ws.send(JSON.stringify({ type: "ready", to: msg.from }));
          } else if (msg.type === "peer-left") {
            setHostOnline(false);
            setStatus("waiting-for-host");
          } else if (msg.type === "offer") {
            await pc.setRemoteDescription(msg.sdp);
            for (const c of iceQueue) await pc.addIceCandidate(c);
            iceQueue = [];
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            ws.send(JSON.stringify({ type: "answer", to: msg.from, sdp: pc.localDescription }));
          } else if (msg.type === "ice") {
            const cand = new RTCIceCandidate(msg.candidate);
            if (pc.remoteDescription) await pc.addIceCandidate(cand);
            else iceQueue.push(cand);
          }
        },
      });
      wsRef.current = ws;
      ws.onopen = () => setStatus("waiting-for-host");
    }
    setup();

    return () => {
      alive = false;
      if (wsRef.current) wsRef.current.close();
      if (pcRef.current) pcRef.current.close();
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [id]);

  const toggleCam = () => {
    if (!streamRef.current) return;
    streamRef.current.getVideoTracks().forEach((t) => (t.enabled = !t.enabled));
    setCamOn((v) => !v);
  };
  const toggleMic = () => {
    if (!streamRef.current) return;
    streamRef.current.getAudioTracks().forEach((t) => (t.enabled = !t.enabled));
    setMicOn((v) => !v);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa] flex flex-col" data-testid="live-join-root">
      <div className="glass border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[#666] hover:text-white" data-testid="live-exit"><X size={18} /></Link>
          <div>
            <p className="label-eyebrow">Live · Guest</p>
            <h2 className="font-serif text-lg leading-tight">{meeting?.title || "Boardroom session"}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className={`h-2 w-2 rounded-full ${status === "connected" ? "bg-emerald-400 animate-pulse" : "bg-[#c89f45]"}`} />
          <span className="uppercase tracking-widest text-[#888]" data-testid="live-status">{status.replace(/-/g, " ")}</span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 p-6 lg:p-10">
        <div className="relative aspect-video rounded-md overflow-hidden bg-[#080808] border border-[#2a2a2a]" data-testid="live-host-video">
          <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
          <div className="absolute top-3 left-3 glass px-3 py-1 rounded">
            <p className="label-eyebrow text-[#c89f45]">Host</p>
          </div>
          {!hostOnline && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <Phone size={20} className="text-[#c89f45] mb-3" />
              <p className="font-serif text-xl">Waiting for the chair to call to order…</p>
              <p className="text-xs text-[#666] mt-2">The host's video will appear here when their session is live.</p>
            </div>
          )}
        </div>
        <div className="relative aspect-video rounded-md overflow-hidden bg-[#080808] border border-[#2a2a2a]" data-testid="live-guest-video">
          <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
          <div className="absolute top-3 left-3 glass px-3 py-1 rounded">
            <p className="label-eyebrow text-[#c89f45]">You · Guest</p>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 glass rounded-full px-2 py-1 flex items-center gap-2">
            <button onClick={toggleMic} className={`h-9 w-9 rounded-full flex items-center justify-center ${micOn ? "bg-[#1a1a1a]" : "bg-red-900"}`} data-testid="live-toggle-mic">
              {micOn ? <Mic size={14} /> : <MicOff size={14} />}
            </button>
            <button onClick={toggleCam} className={`h-9 w-9 rounded-full flex items-center justify-center ${camOn ? "bg-[#1a1a1a]" : "bg-red-900"}`} data-testid="live-toggle-cam">
              {camOn ? <Video size={14} /> : <VideoOff size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
